/**
 * 北京CA (BJCA) 登录工具
 *
 * 基于 XTXASynObject (WebSocket 模式) 实现，统一暴露以下方法：
 *   - getCertList()      获取证书列表
 *   - selectCert(certId) 选择/锁定某个证书
 *   - getUserCert(certId) 导出用户证书原文 (base64)
 *   - signData(certId, plain) 用私钥对原文做 P1 签名
 *   - isLoggedIn(certId) 检查该证书是否已登录
 *   - ensureReady()      确保 WebSocket 已连接（其它方法内部会自动调用）
 *
 * 现阶段不调用后端登录接口，只负责把 CA 信息获取出来。
 * 后续后端出登录接口后，可在 caLoginHelper 基础上补充 signLoginPayload。
 */

import { XTXASynObject } from '@/api/ca/XTXASynObject'

let _xtxObject = null

function getXtxObject() {
  if (!_xtxObject) {
    _xtxObject = new XTXASynObject()
  }
  return _xtxObject
}

/**
 * 确保 WebSocket 已建立连接
 */
async function ensureReady() {
  const xtx = getXtxObject()
  await xtx.initWebSocket()
  return xtx
}

/**
 * 获取证书列表（已插入的 USBKey 列表）
 * @returns {Promise<Array<{name: string, certid: string}>>}
 */
async function getCertList() {
  const xtx = await ensureReady()
  const list = await xtx.SOF_GetUserList()
  return Array.isArray(list) ? list : []
}

/**
 * 导出用户证书 (X509 证书, base64 字符串)
 * @param {string} certId
 */
async function getUserCert(certId) {
  if (!certId) {
    throw new Error('certId 不能为空')
  }
  const xtx = await ensureReady()
  return await xtx.SOF_ExportUserCert(certId)
}

/**
 * 用私钥对 plain 做 P1 签名（base64）
 * @param {string} certId
 * @param {string} plain
 */
async function signData(certId, plain) {
  if (!certId) {
    throw new Error('certId 不能为空')
  }
  if (plain === undefined || plain === null) {
    throw new Error('待签名原文不能为空')
  }
  const xtx = await ensureReady()
  return await xtx.SOF_SignDataBase64(certId, plain)
}

/**
 * 检查某个证书是否已登录（已扫码/已解锁）
 * @param {string} certId
 * @returns {Promise<boolean>}
 */
async function isLoggedIn(certId) {
  if (!certId) return false
  try {
    const xtx = await ensureReady()
    const ret = await xtx.SOF_IsLogin(certId)
    return String(ret) === 'true'
  } catch (e) {
    console.warn('[BJCA] 检查登录状态失败:', e)
    return false
  }
}

export const bjcaLogin = {
  /** CA 类型标识 */
  caType: 'BJCA',
  ensureReady,
  getCertList,
  getUserCert,
  signData,
  isLoggedIn
}

export default bjcaLogin
